/**
 * Settings Hook
 * Reusable hooks for site settings management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/api/api.client';
import { API_ENDPOINTS } from '@/api/endpoints';
import type { Setting, ApiResponse } from '@/api/types';

// Query keys
export const SETTINGS_KEY = ['settings'];
export const PUBLIC_SETTINGS_KEY = ['settings', 'public'];
export const SETTING_KEY = (key: string) => ['settings', key];

// Common setting keys for easy access
export const SETTING_KEYS = {
  WHATSAPP_NUMBER: 'WHATSAPP_NUMBER',
  EMAIL: 'EMAIL',
  PHONE: 'PHONE',
  PHONE_2: 'PHONE_2',
  ADDRESS: 'ADDRESS',
  GOOGLE_MAPS_EMBED_URL: 'GOOGLE_MAPS_EMBED_URL',
  FACEBOOK_URL: 'FACEBOOK_URL',
  INSTAGRAM_URL: 'INSTAGRAM_URL',
  X_URL: 'X_URL',
  YOUTUBE_URL: 'YOUTUBE_URL',
  LINKEDIN_URL: 'LINKEDIN_URL',
  // Payment Settings (for donation emails)
  LUMICASH_NUMBER: 'LUMICASH_NUMBER',
  ECOCASH_NUMBER: 'ECOCASH_NUMBER',
  BANK_NAME: 'BANK_NAME',
  BANK_ACCOUNT: 'BANK_ACCOUNT',
  BANK_ACCOUNT_HOLDER: 'BANK_ACCOUNT_HOLDER',
  // Year Theme (Annual slogan displayed on homepage)
  YEAR_THEME_TITLE_FR: 'YEAR_THEME_TITLE_FR',
  YEAR_THEME_TITLE_EN: 'YEAR_THEME_TITLE_EN',
  YEAR_THEME_SUBTITLE_FR: 'YEAR_THEME_SUBTITLE_FR',
  YEAR_THEME_SUBTITLE_EN: 'YEAR_THEME_SUBTITLE_EN',
  // Reply-To Email Redirect (admin-only)
  REPLY_TO_ENABLED: 'REPLY_TO_ENABLED',
  REPLY_TO_EMAIL: 'REPLY_TO_EMAIL',
  // Site Banner (public announcement bar)
  BANNER_ENABLED: 'BANNER_ENABLED',
  BANNER_MESSAGE_FR: 'BANNER_MESSAGE_FR',
  BANNER_MESSAGE_EN: 'BANNER_MESSAGE_EN',
  BANNER_LINK_TEXT_FR: 'BANNER_LINK_TEXT_FR',
  BANNER_LINK_TEXT_EN: 'BANNER_LINK_TEXT_EN',
  BANNER_LINK_URL: 'BANNER_LINK_URL',
  // Killswitches (super-admin only)
  KILLSWITCH_PUBLIC: 'KILLSWITCH_PUBLIC',
  KILLSWITCH_ADMIN: 'KILLSWITCH_ADMIN',
} as const;

// Backend returns settings as an object, not an array
interface SettingsApiResponse {
  success: boolean;
  data: Record<string, string>;
  meta: {
    timestamp: string;
    path: string;
  };
}

/**
 * Fetch all settings
 * Backend returns { key: value } object, we transform to array
 */
export function useSettings() {
  return useQuery<SettingsApiResponse, Error, Setting[]>({
    queryKey: SETTINGS_KEY,
    queryFn: async () => {
      const response = await apiClient.get<SettingsApiResponse>(
        API_ENDPOINTS.SETTINGS.BASE
      );
      return response.data;
    },
    select: (apiResponse) => {
      // Transform object { KEY: value } to array [{ key: KEY, value: value }]
      return Object.entries(apiResponse.data).map(([key, value]) => ({
        key,
        value,
        updatedAt: new Date().toISOString(), // Default value as backend may not return this
      }));
    },
    staleTime: 60000, // Settings don't change often
  });
}

/**
 * Fetch a single setting by key
 */
export function useSetting(key: string) {
  return useQuery<ApiResponse<Setting>, Error, Setting>({
    queryKey: SETTING_KEY(key),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Setting>>(
        API_ENDPOINTS.SETTINGS.BY_KEY(key)
      );
      return response.data;
    },
    select: (data) => data.data,
    enabled: !!key,
  });
}

/**
 * Update a single setting
 */
export function useUpdateSetting() {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<Setting>, Error, { key: string; value: string }>({
    mutationFn: async ({ key, value }) => {
      const response = await apiClient.patch<ApiResponse<Setting>>(
        API_ENDPOINTS.SETTINGS.BY_KEY(key),
        { value }
      );
      return response.data;
    },
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
      queryClient.invalidateQueries({ queryKey: SETTING_KEY(key) });
    },
  });
}

/**
 * Update multiple settings at once
 */
export function useBulkUpdateSettings() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { key: string; value: string }[]>({
    mutationFn: async (settings) => {
      // Update settings sequentially (backend may not support bulk)
      for (const { key, value } of settings) {
        await apiClient.patch(API_ENDPOINTS.SETTINGS.BY_KEY(key), { value });
      }
    },
    onSuccess: () => {
      // Invalidate both admin and public settings caches
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
      queryClient.invalidateQueries({ queryKey: PUBLIC_SETTINGS_KEY });
    },
  });
}

/**
 * Transform settings array to a key-value map for easier access
 */
export function useSettingsMap() {
  const { data: settings, ...rest } = useSettings();

  const settingsMap = settings?.reduce((acc, setting) => {
    acc[setting.key] = setting.value;
    return acc;
  }, {} as Record<string, string>) ?? {};

  return { data: settingsMap, ...rest };
}

// ============================================
// PUBLIC SETTINGS (No authentication required)
// ============================================

/**
 * Public settings response type
 */
export interface PublicSettings {
  EMAIL: string;
  PHONE: string;
  PHONE_2: string;
  WHATSAPP_NUMBER: string;
  ADDRESS: string;
  GOOGLE_MAPS_EMBED_URL: string;
  FACEBOOK_URL: string;
  INSTAGRAM_URL: string;
  X_URL: string;
  YOUTUBE_URL: string;
  LINKEDIN_URL: string;
  // Year Theme
  YEAR_THEME_TITLE_FR: string;
  YEAR_THEME_TITLE_EN: string;
  YEAR_THEME_SUBTITLE_FR: string;
  YEAR_THEME_SUBTITLE_EN: string;
  // Site Banner
  BANNER_ENABLED: string;
  BANNER_MESSAGE_FR: string;
  BANNER_MESSAGE_EN: string;
  BANNER_LINK_TEXT_FR: string;
  BANNER_LINK_TEXT_EN: string;
  BANNER_LINK_URL: string;
  KILLSWITCH_PUBLIC: string;
  KILLSWITCH_ADMIN: string;
}

/**
 * Fetch public settings (no auth required)
 * Used by public pages for contact info and social links
 */
export function usePublicSettings() {
  return useQuery<SettingsApiResponse, Error, PublicSettings>({
    queryKey: PUBLIC_SETTINGS_KEY,
    queryFn: async () => {
      const response = await apiClient.get<SettingsApiResponse>(
        API_ENDPOINTS.SETTINGS.PUBLIC
      );
      return response.data;
    },
    select: (apiResponse) => apiResponse.data as unknown as PublicSettings,
    staleTime: 60000, // Settings don't change often
  });
}

// ============================================
// DAILY INSPIRATION
// ============================================

export type InspirationSource = 'bible' | 'quran' | 'leadership';

export interface DailyInspiration {
  source: InspirationSource;
  content: string;
  reference?: string;
  date: string;
  isTruncated?: boolean; // If content was truncated for UX
}

export const INSPIRATION_KEY = ['settings', 'inspiration'];

/**
 * Fetch daily inspiration (Bible, Quran, or Leadership quote)
 * Changes daily and is cached for 24 hours
 */
export function useDailyInspiration() {
  return useQuery<{ data: DailyInspiration }, Error, DailyInspiration>({
    queryKey: INSPIRATION_KEY,
    queryFn: async () => {
      const response = await apiClient.get<{ data: DailyInspiration }>(
        API_ENDPOINTS.SETTINGS.INSPIRATION
      );
      return response.data;
    },
    select: (data) => data.data,
    staleTime: 1000 * 60 * 60, // 1 hour - inspiration is cached for 24h on backend
    gcTime: 1000 * 60 * 60 * 2, // 2 hours cache
    refetchOnWindowFocus: false,
  });
}

// ============================================
// CACHE MANAGEMENT
// ============================================

interface FlushCacheResponse {
  success: boolean;
  message: string;
  keysCleared?: number;
}

/**
 * Flush all backend cache
 * Admin only - clears all cached data
 */
export function useFlushCache() {
  const queryClient = useQueryClient();

  return useMutation<FlushCacheResponse, Error>({
    mutationFn: async () => {
      const response = await apiClient.post<{ data: FlushCacheResponse }>(
        `${API_ENDPOINTS.SETTINGS.BASE}/cache/flush`
      );
      return response.data.data;
    },
    onSuccess: () => {
      // Also invalidate all React Query caches
      queryClient.invalidateQueries();
    },
  });
}

// ============================================
// KILLSWITCH (SUPER_ADMIN only)
// ============================================

export const KILLSWITCH_KEY = ['settings', 'killswitch'];

interface KillswitchStatus {
  public: boolean;
  admin: boolean;
}

export function useKillswitchStatus() {
  return useQuery<{ data: KillswitchStatus }, Error, KillswitchStatus>({
    queryKey: KILLSWITCH_KEY,
    queryFn: async () => {
      const response = await apiClient.get<{ data: KillswitchStatus }>(
        `${API_ENDPOINTS.SETTINGS.BASE}/killswitch`
      );
      return response.data;
    },
    select: (data) => data.data,
    staleTime: 30000,
  });
}

export function useToggleKillswitch() {
  const queryClient = useQueryClient();

  return useMutation<
    { data: { message: string } },
    Error,
    { target: 'public' | 'admin'; enabled: boolean }
  >({
    mutationFn: async (body) => {
      const response = await apiClient.patch<{ data: { message: string } }>(
        `${API_ENDPOINTS.SETTINGS.BASE}/killswitch`,
        body
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KILLSWITCH_KEY });
      queryClient.invalidateQueries({ queryKey: PUBLIC_SETTINGS_KEY });
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });
}

export default useSettings;

